import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useCreateShare } from "../../hooks/useCreateShare";
import { useThumbnails } from "../../hooks/useThumbnails";
import { AuthService } from "../../services/auth";
import { formatClipTime } from "../../utils/time";
import { useI18n } from "../../i18n";
import { CloseIcon, PlayIcon } from "../icons";
import VideoFrame from "./VideoFrame";

type VideoPlayerModalProps = {
  isOpen: boolean;
  videoUrl: string | null;
  videoKey: string | null;
  onClose: () => void;
};

const SHARE_EXPIRE_DAYS = 7;
const MIN_CLIP_SECONDS = 1;
// Shared clips are capped at 15 minutes (the backend enforces the same limit).
const MAX_CLIP_SECONDS = 15 * 60;
// Fine-adjust step for the ± nudge controls on each bound.
const NUDGE_SECONDS = 0.5;
// Padding (as a fraction of the clip) shown around the window when zoomed in,
// so the handles have room to breathe instead of touching the timeline edges.
const ZOOM_PADDING_RATIO = 0.35;
// Floor on the zoomed view span so a 1s clip still fills a usable strip.
const ZOOM_MIN_SPAN_SECONDS = 6;

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

type DragTarget = "start" | "end" | "move" | "scrub" | null;

// The slice of the source mapped onto the visible timeline. Unzoomed this is
// the whole video; zoomed it is a padded window around the clip so the handles
// are big and grabbable on a phone even for a long source.
type View = { start: number; span: number };

type TrimmerProps = {
  duration: number;
  start: number;
  end: number;
  current: number;
  maxLength: number;
  view: View;
  thumbnails: string[];
  onChange: (start: number, end: number) => void;
  onScrub: (time: number) => void;
};

// A filmstrip of evenly-spaced thumbnails covering the whole source, clipped to
// the visible window so it pans/zooms with the timeline. Falls back to a calm
// banded gradient (deliberate, not a broken bar) when no thumbnails exist.
function Filmstrip({
  duration,
  view,
  thumbnails,
}: {
  duration: number;
  view: View;
  thumbnails: string[];
}) {
  if (thumbnails.length === 0 || !duration) {
    return (
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[repeating-linear-gradient(135deg,var(--color-base-300)_0_14px,var(--color-base-200)_14px_28px)] opacity-70"
      />
    );
  }
  // Spread the thumbnails evenly across the full duration, then translate/scale
  // that row into the view window. Decorative orientation, not frame-accurate.
  const scale = duration / view.span;
  const offset = (-view.start / duration) * 100 * scale;
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-y-0 flex"
        style={{ width: `${scale * 100}%`, left: `${offset}%` }}
      >
        {thumbnails.map((src, i) => (
          <div
            key={i}
            className="h-full flex-1 bg-cover bg-center"
            style={{ backgroundImage: `url("${src}")` }}
          />
        ))}
      </div>
      <div className="absolute inset-0 bg-base-content/15" />
    </div>
  );
}

// Clip trimmer over a (possibly zoomed) view window. Tap/drag the track to move
// the playhead; drag the highlighted window to slide the clip or an edge handle
// to resize it (capped at maxLength). Keyboard: focus the window to move it, or
// a handle to resize -- arrows step 1s, Shift = 5s. A live playhead tracks
// playback. All position math runs through the view window so it stays correct
// zoomed or not.
function Trimmer({
  duration,
  start,
  end,
  current,
  maxLength,
  view,
  thumbnails,
  onChange,
  onScrub,
}: TrimmerProps) {
  const { t } = useI18n();
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragTarget>(null);
  // For a window drag: distance from the pointer to the clip start, so the
  // window slides under the finger instead of snapping its start to it.
  const grabOffsetRef = useRef(0);
  const length = end - start;

  // While zoomed, the parent recomputes `view` from start/end -- which a drag is
  // actively changing -- so the window would recenter under the finger every
  // move. Snapshot the view at pointer-down and use it for the whole drag so the
  // time<->pixel mapping stays fixed; release back to the live view on pointer-up.
  const [frozenView, setFrozenView] = useState<View | null>(null);
  const activeView = frozenView ?? view;

  // Map a time to a percentage within the visible window (may exceed 0-100 for
  // times outside the window; callers clamp where it matters).
  const pct = (v: number) => ((v - activeView.start) / activeView.span) * 100;

  const valueFromX = useCallback(
    (clientX: number): number => {
      const el = trackRef.current;
      if (!el || !duration) return 0;
      const r = el.getBoundingClientRect();
      const ratio = clamp((clientX - r.left) / r.width, 0, 1);
      return clamp(activeView.start + ratio * activeView.span, 0, duration);
    },
    [duration, activeView.start, activeView.span],
  );

  // Begin a drag: remember its target and freeze the view for its duration.
  const beginDrag = (target: Exclude<DragTarget, null>) => {
    dragRef.current = target;
    setFrozenView(view);
  };

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const target = dragRef.current;
      if (!target) return;
      const v = valueFromX(e.clientX);
      if (target === "scrub") {
        onScrub(v);
      } else if (target === "move") {
        // Slide the whole window, preserving its length.
        const ns = clamp(
          v - grabOffsetRef.current,
          0,
          Math.max(0, duration - length),
        );
        onChange(ns, ns + length);
        onScrub(ns);
      } else if (target === "start") {
        // Resize: start can't fall more than maxLength before the fixed end.
        const ns = clamp(
          v,
          Math.max(0, end - maxLength),
          end - MIN_CLIP_SECONDS,
        );
        onChange(ns, end);
        onScrub(ns);
      } else {
        const ne = clamp(
          v,
          start + MIN_CLIP_SECONDS,
          Math.min(duration, start + maxLength),
        );
        onChange(start, ne);
        onScrub(ne);
      }
    };
    const up = () => {
      dragRef.current = null;
      setFrozenView(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [duration, start, end, length, maxLength, onChange, onScrub, valueFromX]);

  const onWindowKey = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 5 : 1;
    let ns = start;
    if (e.key === "ArrowLeft") ns = Math.max(0, start - step);
    else if (e.key === "ArrowRight")
      ns = Math.min(Math.max(0, duration - length), start + step);
    else return;
    e.preventDefault();
    onChange(ns, ns + length);
    onScrub(ns);
  };

  const onHandleKey = (which: "start" | "end") => (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 5 : 1;
    let ns = start;
    let ne = end;
    if (e.key === "ArrowLeft") {
      if (which === "start") ns = Math.max(0, end - maxLength, start - step);
      else ne = Math.max(start + MIN_CLIP_SECONDS, end - step);
    } else if (e.key === "ArrowRight") {
      if (which === "start")
        ns = Math.min(end - MIN_CLIP_SECONDS, start + step);
      else ne = Math.min(duration, start + maxLength, end + step);
    } else {
      return;
    }
    e.preventDefault();
    onChange(ns, ne);
    onScrub(which === "start" ? ns : ne);
  };

  const handle = (which: "start" | "end", value: number) => (
    <div
      role="slider"
      aria-label={
        which === "start" ? t("trimmer.clipStart") : t("trimmer.clipEnd")
      }
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(value)}
      aria-valuetext={formatClipTime(value)}
      tabIndex={0}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        beginDrag(which);
        onScrub(value);
      }}
      onKeyDown={onHandleKey(which)}
      className="absolute top-1/2 z-20 flex h-[3.75rem] w-7 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize touch-none items-center justify-center rounded-field bg-primary shadow-md ring-1 ring-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-base-100"
      style={{ left: clamp(pct(value), 0, 100) + "%" }}
    >
      <span className="h-6 w-1 rounded-full bg-primary-content/80"></span>
    </div>
  );

  return (
    <div className="select-none">
      <div
        ref={trackRef}
        onPointerDown={(e) => {
          // Tap or drag empty track to move the playhead -- "find the moment".
          beginDrag("scrub");
          onScrub(valueFromX(e.clientX));
        }}
        className="relative h-16 cursor-pointer touch-none overflow-hidden rounded-box bg-base-300 ring-1 ring-base-content/10"
      >
        <Filmstrip
          duration={duration}
          view={activeView}
          thumbnails={thumbnails}
        />

        {/* Dim the regions outside the clip so the window reads as the keeper. */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 bg-base-300/70"
          style={{ width: clamp(pct(start), 0, 100) + "%" }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 bg-base-300/70"
          style={{ width: clamp(100 - pct(end), 0, 100) + "%" }}
        />

        {/* Draggable clip window */}
        <div
          role="slider"
          aria-label={t("trimmer.clipPosition")}
          aria-valuemin={0}
          aria-valuemax={Math.round(Math.max(0, duration - length))}
          aria-valuenow={Math.round(start)}
          aria-valuetext={`${formatClipTime(start)} to ${formatClipTime(end)}`}
          tabIndex={0}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            beginDrag("move");
            grabOffsetRef.current = valueFromX(e.clientX) - start;
            onScrub(start);
          }}
          onKeyDown={onWindowKey}
          className="absolute inset-y-0 z-10 flex cursor-grab touch-none items-center justify-center border-x-2 border-y-[3px] border-primary bg-primary/15 active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
          style={{
            left: clamp(pct(start), 0, 100) + "%",
            width: clamp(pct(end), 0, 100) - clamp(pct(start), 0, 100) + "%",
          }}
        >
          {/* grip affordance */}
          <div className="pointer-events-none flex gap-1 rounded-field bg-primary/80 px-1.5 py-2">
            <span className="h-4 w-0.5 rounded-full bg-primary-content/80"></span>
            <span className="h-4 w-0.5 rounded-full bg-primary-content/80"></span>
            <span className="h-4 w-0.5 rounded-full bg-primary-content/80"></span>
          </div>
        </div>

        {/* Live playhead */}
        <div
          className="pointer-events-none absolute inset-y-0 z-20 w-0.5 -translate-x-1/2 bg-base-content shadow-[0_0_0_1px_rgba(255,255,255,0.6)]"
          style={{
            left: clamp(pct(current), 0, 100) + "%",
            opacity:
              current >= view.start && current <= view.start + view.span
                ? 1
                : 0,
          }}
        >
          <span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-[1px] bg-base-content" />
        </div>

        {handle("start", start)}
        {handle("end", end)}
      </div>
    </div>
  );
}

// A bound (start/end) row: a labelled "Set" button that snaps it to the
// playhead, plus ± half-second nudge steppers. Decouples "find the moment"
// (scrub) from "set the window" so a 60s clip is selectable on any source.
function BoundControl({
  label,
  value,
  onSet,
  onNudge,
  canMinus,
  canPlus,
}: {
  label: string;
  value: number;
  onSet: () => void;
  onNudge: (delta: number) => void;
  canMinus: boolean;
  canPlus: boolean;
}) {
  const { t } = useI18n();
  return (
    <div className="flex-1 rounded-box bg-base-200 p-2">
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-medium opacity-70">{label}</span>
        <span className="font-display text-sm tabular-nums">
          {formatClipTime(value)}
        </span>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onNudge(-NUDGE_SECONDS)}
          disabled={!canMinus}
          aria-label={t("trimmer.nudgeBack", { label })}
          className="btn btn-sm btn-square btn-ghost text-lg leading-none"
        >
          –
        </button>
        <button
          type="button"
          onClick={onSet}
          className="btn btn-sm flex-1 btn-outline btn-primary"
        >
          {t("trimmer.setBound", { label: label.toLowerCase() })}
        </button>
        <button
          type="button"
          onClick={() => onNudge(NUDGE_SECONDS)}
          disabled={!canPlus}
          aria-label={t("trimmer.nudgeForward", { label })}
          className="btn btn-sm btn-square btn-ghost text-lg leading-none"
        >
          +
        </button>
      </div>
    </div>
  );
}

export default function VideoPlayerModal({
  isOpen,
  videoUrl,
  videoKey,
  onClose,
}: VideoPlayerModalProps) {
  const { t } = useI18n();
  const auth = useAuth();
  // Only admins create and manage clips; everyone else just watches.
  const isAdmin = AuthService.isAdmin(auth);
  const modalRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { createShareLink, isLoading: isCreating, error } = useCreateShare();
  const [duration, setDuration] = useState(0);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [current, setCurrent] = useState(0);
  const [previewing, setPreviewing] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Filmstrip thumbnails for the open video (same source the cards use). Only
  // fetched while the modal is open so closed modals make no requests.
  const { thumbnails } = useThumbnails(videoKey ?? "", {
    enabled: isOpen && videoKey !== null && isAdmin,
  });

  // Reset clip selection whenever a video is opened -- keyed on isOpen too so
  // reopening the same video doesn't keep a stale share link or clip range.
  useEffect(() => {
    setDuration(0);
    setStart(0);
    setEnd(0);
    setCurrent(0);
    setPreviewing(false);
    setZoomed(false);
    setShareUrl(null);
    setCopied(false);
  }, [videoUrl, isOpen]);

  // Close modal when clicking outside the video player.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  // Visible timeline window: whole source, or a padded slice around the clip
  // when zoomed so the handles become large on a long source. Memoised so the
  // Trimmer's pointer math sees a stable reference per render.
  const view: View = useMemo(() => {
    if (!zoomed || !duration) return { start: 0, span: duration || 1 };
    const pad = Math.max(
      ZOOM_PADDING_RATIO * (end - start),
      (ZOOM_MIN_SPAN_SECONDS - (end - start)) / 2,
      0,
    );
    const vStart = Math.max(0, start - pad);
    const vEnd = Math.min(duration, end + pad);
    return { start: vStart, span: Math.max(0.001, vEnd - vStart) };
  }, [zoomed, duration, start, end]);

  if (!isOpen || !videoUrl) return null;

  const clipLength = Math.max(0, end - start);
  const canCreate =
    videoKey !== null &&
    clipLength >= MIN_CLIP_SECONDS &&
    clipLength <= MAX_CLIP_SECONDS;

  const handleLoadedMetadata = () => {
    const d = videoRef.current?.duration;
    const safeDuration = Number.isFinite(d) ? (d as number) : 0;
    setDuration(safeDuration);
    setStart(0);
    setEnd(
      Math.min(safeDuration, MAX_CLIP_SECONDS, Math.max(5, safeDuration * 0.4)),
    );
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    setCurrent(video.currentTime);
    if (previewing && video.currentTime >= end) {
      video.pause();
      setPreviewing(false);
    }
  };

  const setRange = (s: number, e: number) => {
    // Final guard so the selection can never exceed the cap.
    setStart(s);
    setEnd(Math.min(e, s + MAX_CLIP_SECONDS));
    setShareUrl(null);
  };

  const scrub = (time: number) => {
    setCurrent(time);
    if (videoRef.current) videoRef.current.currentTime = time;
  };

  // Limits for each bound, derived once so set/nudge and the button-enable
  // guards share one source of truth (start lives in [startMin, startMax], end
  // in [endMin, endMax]) and can't drift apart.
  const startMin = Math.max(0, end - MAX_CLIP_SECONDS);
  const startMax = end - MIN_CLIP_SECONDS;
  const endMin = start + MIN_CLIP_SECONDS;
  const endMax = Math.min(duration, start + MAX_CLIP_SECONDS);

  // Snap a bound to the current playhead, respecting those limits.
  const setStartToPlayhead = () =>
    setRange(clamp(current, startMin, startMax), end);
  const setEndToPlayhead = () =>
    setRange(start, clamp(current, endMin, endMax));

  const nudgeStart = (delta: number) => {
    const s = clamp(start + delta, startMin, startMax);
    setRange(s, end);
    scrub(s);
  };
  const nudgeEnd = (delta: number) => {
    const e = clamp(end + delta, endMin, endMax);
    setRange(start, e);
    scrub(e);
  };

  const previewClip = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = start;
    void video.play();
    setPreviewing(true);
  };

  const handleCreateLink = async () => {
    if (!canCreate || videoKey === null) return;
    const url = await createShareLink({
      videoKey,
      start,
      end,
      expiresInDays: SHARE_EXPIRE_DAYS,
    });
    if (url) setShareUrl(url);
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (insecure context or denied); the field stays
      // selectable so the user can copy manually.
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-[fade-in_150ms_ease-out]">
      <div ref={modalRef} className="relative w-full max-w-5xl">
        <button
          onClick={onClose}
          className="absolute -top-12 sm:-top-10 right-0 text-white/80 hover:text-white transition-colors p-2 touch-manipulation"
          aria-label={t("trimmer.closeVideo")}
        >
          <CloseIcon className="h-6 w-6 sm:h-8 sm:w-8" />
        </button>

        {/* Scroll the content on short screens without clipping the close button, which sits above this box. */}
        <div className="max-h-[100dvh] overflow-y-auto">
          <VideoFrame>
            <video
              ref={videoRef}
              className="absolute top-0 left-0 w-full h-full"
              controls
              autoPlay
              playsInline
              src={videoUrl}
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
            >
              <p>
                {t("trimmer.videoUnsupported")}{" "}
                <a href={videoUrl} download={videoUrl}>
                  {t("trimmer.downloadInstead")}
                </a>
              </p>
            </video>
          </VideoFrame>

          {/* Clip trimmer + share -- only admins create and manage clips */}
          {isAdmin && (
            <div className="mt-3 bg-base-100 rounded-box p-3 sm:p-4 shadow-lg">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium font-display">
                  {t("trimmer.heading")}
                </p>
                <span className="text-xs tabular-nums opacity-70">
                  {formatClipTime(start)} – {formatClipTime(end)} ·{" "}
                  <span className="font-medium">
                    {formatClipTime(clipLength)}
                  </span>
                </span>
              </div>

              <div className="mt-2.5">
                <Trimmer
                  duration={duration}
                  start={start}
                  end={end}
                  current={current}
                  maxLength={MAX_CLIP_SECONDS}
                  view={view}
                  thumbnails={thumbnails}
                  onChange={setRange}
                  onScrub={scrub}
                />
                <div className="mt-1.5 flex items-center justify-between gap-2 text-xs opacity-60">
                  <span>{formatClipTime(view.start)}</span>
                  <span className="opacity-80">
                    {zoomed
                      ? t("trimmer.zoomedLabel")
                      : t("trimmer.wholeLabel")}
                  </span>
                  <span>{formatClipTime(view.start + view.span)}</span>
                </div>
              </div>

              {/* Find the moment, then set a bound to it. */}
              <div className="mt-3 flex gap-2">
                <BoundControl
                  label={t("trimmer.start")}
                  value={start}
                  onSet={setStartToPlayhead}
                  onNudge={nudgeStart}
                  // Minus moves start earlier (lengthens); plus moves it later
                  // (shortens). Enable exactly while start can move that way.
                  canMinus={start > startMin}
                  canPlus={start < startMax}
                />
                <BoundControl
                  label={t("trimmer.end")}
                  value={end}
                  onSet={setEndToPlayhead}
                  onNudge={nudgeEnd}
                  canMinus={end > endMin}
                  canPlus={end < endMax}
                />
              </div>

              <p className="text-xs opacity-50 mt-2">
                {t("trimmer.instructions")}
              </p>

              <div className="flex flex-wrap items-center gap-2 mt-3">
                <button
                  onClick={previewClip}
                  disabled={clipLength < MIN_CLIP_SECONDS}
                  className="btn btn-sm btn-outline gap-2"
                >
                  <PlayIcon className="w-4 h-4" />
                  {t("trimmer.preview")}
                </button>
                <button
                  type="button"
                  onClick={() => setZoomed((z) => !z)}
                  disabled={duration === 0}
                  aria-pressed={zoomed}
                  className="btn btn-sm btn-ghost gap-2"
                >
                  {zoomed ? t("trimmer.zoomOut") : t("trimmer.zoomIn")}
                </button>
                {!shareUrl && (
                  <button
                    onClick={handleCreateLink}
                    disabled={!canCreate || isCreating}
                    className="btn btn-primary btn-sm ml-auto"
                  >
                    {isCreating ? (
                      <span className="loading loading-spinner loading-xs"></span>
                    ) : (
                      t("trimmer.createLink")
                    )}
                  </button>
                )}
              </div>

              {error && (
                <p className="text-error text-xs mt-2">
                  {t("trimmer.createError")}
                </p>
              )}

              {shareUrl && (
                <div className="mt-3">
                  <div className="join w-full">
                    <input
                      type="text"
                      readOnly
                      value={shareUrl}
                      onFocus={(e) => e.currentTarget.select()}
                      aria-label={t("trimmer.linkAria")}
                      className="input input-sm input-bordered join-item flex-1 text-xs"
                    />
                    <button
                      onClick={copyLink}
                      className="btn btn-sm join-item btn-primary"
                    >
                      {copied ? t("common.copied") : t("common.copy")}
                    </button>
                  </div>
                  <p className="text-xs opacity-60 mt-1.5">
                    {t("trimmer.shareInfo", {
                      length: formatClipTime(clipLength),
                      days: SHARE_EXPIRE_DAYS,
                    })}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
